import React, { useCallback, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { NavigateTo, useContainerContext } from '@nextroll/ar-container-context';
import { Logo, BUSINESS_UNIT_ROLLWORKS } from '@nextroll/ar-style-base';

import { IWorkflowTemplate } from 'types/templates';
import { getAppPath } from 'util/urls';
import { WORKFLOW_TEMPLATE_EIDS } from 'util/workflow.constants';

import { WorkflowTemplatesCreateButton } from 'components/basic/WorkflowTemplates/WorkflowTemplatesCreateButton';

import marketoIcon from '../../../assets/icons/marketo.svg';


const TemplateCard = ({ template, navigateTo, handleCreate }: {
    template: IWorkflowTemplate;
    navigateTo: NavigateTo;
    handleCreate: ({ template }: { template?: IWorkflowTemplate }) => void;
}) => {
    const iconSvg = new Blob([template.icon], { type: 'image/svg+xml' });
    const iconUrl = URL.createObjectURL(iconSvg);
    const isNewTemplate = [
        WORKFLOW_TEMPLATE_EIDS.ACQUIRE_NEW_CONTACTS,
        WORKFLOW_TEMPLATE_EIDS.ACQUIRE_HOT_CONTACTS,
    ].includes(template.eid);

    return (
        <section
            key={ `workflow-template-section-${template.eid}`}
            id={`workflows-template-section-${template.eid}`}
            data-testid={`workflows-template-card-${template.entity_type || 'contact'}`}
            className='workflows-templates-card ar-card'
        >
            <div className='ar-card-body' onClick={() => handleCreate({ template })}>
                <img src={iconUrl} height={60} width={60} />
                <div className='card-content template-card-content'>
                    <h4>
                        {template.name}
                        &nbsp;&nbsp;&nbsp;
                        {isNewTemplate && <span className='ar-label-info'>{i18n.gettext('NEW')}</span>}
                    </h4>
                    <span>{template.description}</span>
                    <button
                        type='button'
                        className='btn btn-link'
                        onClick={e => {
                            e.stopPropagation();
                            navigateTo({ to: template.help_center_url, openNewWindow: true });
                        }}
                    >
                        {i18n.gettext('Learn more')}
                        &nbsp;
                        <i className='ar-icon ar-icon-fw fa-external-link-alt' />
                    </button>
                </div>
                <button type='button' className='btn btn-default'>
                    {i18n.gettext('Use Template')}
                </button>
            </div>
        </section>
    );
};

interface IProps {
    templates?: IWorkflowTemplate[];
    onCreate?: () => void;
}

export const WorkflowTemplates = (props: IProps) => {
    const { templates, onCreate } = props;

    const { navigateTo } = useContainerContext();

    // whether the create button menu is open
    const [openMenu, setOpenMenu] = useState(false);

    const handleCreate = useCallback(({ template, base }: { template?: IWorkflowTemplate; base?: string }) => {
        onCreate?.();
        const templateBase = template?.entity_type || base;
        const param = [
            template && `template=${template.eid}`,
            templateBase && `base=${templateBase}`,
        ].filter(Boolean).join('&');
        navigateTo({ to: getAppPath(`/setup${param ? `?${param}` : ''}`) });
    }, [onCreate, navigateTo]);

    /**
     * list of visible tempaltes, or the loading spinner if it is not ready
     */
    const content = useMemo(() => {
        if (!templates) {
            return <div className='ar-page-loader' />;
        }
        return templates
            .filter(template => !template.hidden)
            .map(template => (
                <TemplateCard
                    key={template.eid}
                    template={template}
                    navigateTo={navigateTo}
                    handleCreate={handleCreate}
                />
            ));
    }, [templates, navigateTo, handleCreate]);

    return (
        <div className='workflows-templates'>
            <section className='workflows-templates-card ar-card elevation-1'>
                <div className='ar-card-body' onClick={() => setOpenMenu(true)}>
                    <div className='card-content'>
                        <div className='start-from-scratch-title'>
                            <h3>{i18n.gettext('Start from scratch')}</h3>
                            <i className='fab fa-hubspot fa-lg' aria-hidden />
                            <Logo
                                i18n={i18n}
                                businessUnit={BUSINESS_UNIT_ROLLWORKS}
                                height='16px'
                                wordmark={false}
                                disableRollWorksTheme
                            />
                            <i className='fab fa-salesforce fa-lg' aria-hidden />
                            <img src={marketoIcon} height={18} width={18} aria-hidden />
                        </div>
                        <span>
                            {i18n.gettext('Use AdRoll data in combination with your own data to automate and scale up your Marketing and Sales strategy')}
                        </span>
                    </div>
                    <WorkflowTemplatesCreateButton open={openMenu} setOpen={setOpenMenu} onCreate={handleCreate} />
                </div>
            </section>


            <section className='workflows-templates-title'>
                <h3>{i18n.gettext('Templates')}</h3>
                <p>{i18n.gettext('These templates based on popular use cases will help you get started.')}</p>
            </section>

            {content}
        </div>
    );
};
